import type Keycloak from "keycloak-js";
import type { AuthenticationSession } from "./AuthenticationSession";
import type { OidcConfig } from "./config";
import { saveReturnTo } from "./returnTo";

export class ReauthenticationRequiredError extends Error {
  constructor() {
    super("Reauthentication is required");
    this.name = "ReauthenticationRequiredError";
  }
}

type MinAdapter = Pick<
  Keycloak,
  | "authenticated"
  | "token"
  | "init"
  | "updateToken"
  | "login"
  | "logout"
  | "clearToken"
>;

export class KeycloakAuthenticationSession implements AuthenticationSession {
  private refreshInFlight: Promise<string> | null = null;
  private readonly refreshListeners = new Set<() => void>();

  constructor(
    private readonly config: OidcConfig,
    private readonly adapter: MinAdapter,
  ) {}

  async initialize(): Promise<void> {
    await this.adapter.init({
      onLoad: "login-required",
      flow: "standard",
      pkceMethod: "S256",
      useNonce: true,
      /*
       * Ghim địa chỉ quay về. Không truyền thì keycloak-js rơi về `globalThis.location.href`
       * (adapter mặc định, `keycloak.js:312-313`) — tức lấy "trang đang đứng" làm `redirect_uri`.
       * Điều đó chỉ chạy được khi client khai wildcard `redirectUris`, và làm kết quả uỷ quyền
       * đáp xuống một route sâu thay vì `/`. keycloak-js lưu giá trị này vào `kc.redirectUri`
       * nên nó áp dụng cho mọi lần redirect sau đó, kể cả các nhánh nội bộ của thư viện.
       */
      redirectUri: this.config.redirectUri,
    });
  }

  subscribeTokenRefresh(listener: () => void): () => void {
    this.refreshListeners.add(listener);
    return () => this.refreshListeners.delete(listener);
  }

  private refresh(minValidity: number): Promise<string> {
    if (this.refreshInFlight) return this.refreshInFlight;
    this.refreshInFlight = this.adapter
      .updateToken(minValidity)
      .then((refreshed) => {
        if (!this.adapter.token) throw new ReauthenticationRequiredError();
        if (refreshed) {
          [...this.refreshListeners].forEach((listener) => listener());
        }
        return this.adapter.token;
      })
      .catch(async () => {
        this.adapter.clearToken();
        await this.login();
        throw new ReauthenticationRequiredError();
      })
      .finally(() => {
        this.refreshInFlight = null;
      });
    return this.refreshInFlight;
  }

  getAccessToken(): Promise<string> {
    return this.refresh(30);
  }

  forceRefresh(): Promise<string> {
    return this.refresh(-1);
  }

  async login(): Promise<void> {
    /*
     * Keycloak sẽ trả về `/` (xem `initialize()`), nên vị trí hiện tại phải được nhớ ở phía
     * app TRƯỚC khi rời trang. Đây là điểm nghẽn duy nhất mà app kiểm soát được — keycloak-js
     * còn tự đẩy sang Keycloak ở các nhánh nội bộ (`clearToken()` → `login-required`), và
     * những nhánh đó không đi qua đây.
     *
     * Dạng TƯƠNG ĐỐI là bắt buộc: `sanitizeReturnTo` từ chối mọi giá trị không bắt đầu bằng
     * `/`, nên `location.href` sẽ bị hoá thành `/` và XOÁ mất giá trị đã lưu — hỏng im lặng.
     */
    saveReturnTo(
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    );
    await this.adapter.login({ redirectUri: this.config.redirectUri });
  }

  async logout(): Promise<void> {
    /*
     * KHÔNG dùng `config.redirectUri` ở đây. Giá trị đó bị ghim về `/` cho mọi vòng uỷ quyền
     * (xem `initialize()`), mà `/` nằm ngoài allowlist công khai — hạ cánh ở đó thì `main.tsx`
     * chọn nhánh OIDC và `login-required` đá người dùng sang trang đăng nhập Keycloak. Trỏ về
     * cổng công khai để lượt tải sau đăng xuất không chạm tầng xác thực.
     */
    await this.adapter.logout({ redirectUri: this.config.postLogoutRedirectUri });
  }

  clear(): void {
    this.adapter.clearToken();
    this.refreshInFlight = null;
    this.refreshListeners.clear();
  }
}